import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AppConfigService } from '~/core/services/appconfig.service';
import { GraphService } from '~/core/services/graph.service';
import { AppSettingsService } from '~/core/services/appsettings.service';

/**
 * Guards routes that should only be accessible to members of the admin Azure AD
 * group identified by the `admin_obj_id` setting in config.json.
 *
 * Mirrors the visibility check used in the navigation sidebar so that the route
 * cannot be reached by typing the URL directly. Non-admin users are redirected
 * back to the application home page.
 *
 * The membership check result is memoised for the lifetime of the page so that
 * repeated navigations do not trigger redundant Microsoft Graph requests.
 */
@Injectable({
    providedIn: 'root'
})
export class AdminGuard {
    private readonly cacheStorageKey = 'intel-engine-admin-membership-v1';
    private readonly cacheTtlMs = 10 * 60 * 1000;
    private cachedIsAdmin: Promise<boolean> | null = null;

    constructor(
        private router: Router,
        private appConfigService: AppConfigService,
        private graphService: GraphService,
        private appSettingsService: AppSettingsService,
    ) { }

    public async canActivate(): Promise<boolean> {
        const isAdmin = await this.resolveIsAdmin();

        if (!isAdmin) {
            console.warn('[ADMIN-GUARD] Access denied: user is not a member of the admin group, redirecting to home');
            await this.router.navigate(['/']);
            return false;
        }

        return true;
    }

    private resolveIsAdmin(): Promise<boolean> {
        if (this.cachedIsAdmin) {
            return this.cachedIsAdmin;
        }

        const adminGroupId: string = this.appConfigService.getConfig('admin_obj_id') ?? '';
        if (!adminGroupId) {
            // No admin group configured — fail closed so the route is never exposed by accident.
            this.cachedIsAdmin = Promise.resolve(false);
            return this.cachedIsAdmin;
        }

        const cachedMembership = this.getCachedMembership(adminGroupId);
        if (cachedMembership !== null) {
            this.cachedIsAdmin = Promise.resolve(cachedMembership);
            return this.cachedIsAdmin;
        }

        this.cachedIsAdmin = this.resolveMembershipWithFallback(adminGroupId);
        return this.cachedIsAdmin;
    }

    private async resolveMembershipWithFallback(adminGroupId: string): Promise<boolean> {
        try {
            const appSettings = await this.appSettingsService.getSettings();
            const userRoles: string[] = Array.isArray(appSettings?.user?.roles) ? appSettings.user.roles : [];
            const hasRoleInSession = userRoles.some((role) => String(role).toLowerCase() === adminGroupId.toLowerCase());

            if (hasRoleInSession) {
                this.cacheMembership(adminGroupId, true);
                return true;
            }
        } catch {
        }

        const isAdmin = await this.graphService.checkUserInGroup(adminGroupId);
        this.cacheMembership(adminGroupId, isAdmin);
        return isAdmin;
    }

    private getCachedMembership(adminGroupId: string): boolean | null {
        try {
            const rawCache = sessionStorage.getItem(this.cacheStorageKey);
            if (!rawCache) {
                return null;
            }

            const parsedCache = JSON.parse(rawCache) as {
                adminGroupId?: string;
                isAdmin?: boolean;
                checkedAt?: number;
            };

            if (!parsedCache || parsedCache.adminGroupId !== adminGroupId) {
                return null;
            }

            if (typeof parsedCache.checkedAt !== 'number' || typeof parsedCache.isAdmin !== 'boolean') {
                return null;
            }

            const isExpired = Date.now() - parsedCache.checkedAt > this.cacheTtlMs;
            if (isExpired) {
                sessionStorage.removeItem(this.cacheStorageKey);
                return null;
            }

            return parsedCache.isAdmin;
        } catch {
            return null;
        }
    }

    private cacheMembership(adminGroupId: string, isAdmin: boolean): void {
        try {
            sessionStorage.setItem(this.cacheStorageKey, JSON.stringify({
                adminGroupId,
                isAdmin,
                checkedAt: Date.now()
            }));
        } catch {
        }
    }
}
